import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import {
  Play,
  CalendarClock,
  XCircle,
  Trash2,
  CheckCircle2,
  Activity,
  Mail,
  Phone,
  Clock,
  X,
  DollarSign,
  Calendar,
} from "@/components/icons";
import Portal from "./Portal";
import DateTimePicker from "./DateTimePicker";
import RowActionsMenu from "./RowActionsMenu";
import EmptyState from "./EmptyState";

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

/**
 * AppointmentsTable — column-style appointments list used in Doctor &
 * Secretary overviews.
 *
 * Each row: avatar/iniciales · paciente · fecha+hora · motivo · acciones.
 *
 * Acciones disponibles para citas con status "scheduled":
 *   - Iniciar consulta (sólo doctor, sólo si showIniciar=true)
 *   - Reprogramar (cualquier rol)
 *   - Cancelar (cualquier rol)
 *   - Eliminar (cualquier rol)
 *
 * Las citas completadas o canceladas sólo muestran su chip de estado.
 */
export default function AppointmentsTable({
  appointments,
  patientById,
  showIniciar = false,
  showArrived = false,
  paidAppointmentIds,
  onChanged,
  testid = "appts-table",
  emptyText = "Sin citas.",
}) {
  const navigate = useNavigate();
  const [reschedule, setReschedule] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [starting, setStarting] = useState(null); // appointment.id currently being started
  const [charging, setCharging] = useState(null); // appointment being charged (Cobrar modal)

  // Sort: pending first by date asc, then completed/cancelled at the bottom by date asc
  const orderedAppts = [...(appointments || [])].sort((a, b) => {
    const aDone = a.status === "completed" || a.status === "cancelled";
    const bDone = b.status === "completed" || b.status === "cancelled";
    if (aDone !== bDone) return aDone ? 1 : -1;
    return (a.date || "").localeCompare(b.date || "");
  });

  const cancel = async (a) => {
    if (!window.confirm("¿Cancelar esta cita?")) return;
    try {
      await api.put(`/appointments/${a.id}`, {
        patient_id: a.patient_id,
        office_id: a.office_id,
        date: a.date,
        duration_min: a.duration_min,
        reason: a.reason,
        status: "cancelled",
      });
      toast.success("Cita cancelada");
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const toggleArrived = async (a) => {
    try {
      const { data } = await api.post(`/appointments/${a.id}/arrived`);
      toast.success(
        data.arrived ? "Paciente marcado como llegado" : "Marca de llegada quitada",
      );
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const del = async (a) => {
    if (!window.confirm("¿Eliminar esta cita?")) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Cita eliminada");
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const startConsult = async (a) => {
    setStarting(a.id);
    try {
      // Mark appointment as in_progress, then navigate to consult page.
      // If the PUT fails we still navigate so the doctor isn't blocked.
      await api.put(`/appointments/${a.id}`, {
        patient_id: a.patient_id,
        office_id: a.office_id,
        date: a.date,
        duration_min: a.duration_min,
        reason: a.reason,
        status: "in_progress",
      });
    } catch (_err) {
      /* swallow — navigate anyway */
    } finally {
      setStarting(null);
      navigate(`/doctor/consulta/${a.id}`);
    }
  };

  const markCompleted = async (a) => {
    try {
      await api.put(`/appointments/${a.id}`, {
        patient_id: a.patient_id,
        office_id: a.office_id,
        date: a.date,
        duration_min: a.duration_min,
        reason: a.reason,
        status: "completed",
      });
      toast.success("Cita marcada como atendida");
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const sendReminder = async (a) => {
    try {
      await api.post(`/appointments/${a.id}/send-reminder`);
      toast.success("Recordatorio enviado al paciente");
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const confirmAppt = async (a) => {
    try {
      await api.post(`/appointments/${a.id}/manual-confirm`);
      toast.success("Cita confirmada");
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const openReschedule = (a) => {
    setReschedule(a);
    setNewDate(a.date.slice(0, 16));
  };

  const confirmReschedule = async () => {
    if (!newDate) {
      toast.error("Selecciona la nueva fecha y hora");
      return;
    }
    try {
      await api.put(`/appointments/${reschedule.id}`, {
        patient_id: reschedule.patient_id,
        office_id: reschedule.office_id,
        date: newDate,
        duration_min: reschedule.duration_min,
        reason: reschedule.reason,
        status: "scheduled",
      });
      toast.success("Cita reprogramada");
      setReschedule(null);
      setNewDate("");
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  if (!appointments || appointments.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="w-8 h-8 text-[#0071E3]" strokeWidth={1.75} weight="regular" />}
        title={emptyText}
        description="Añade una nueva cita o cambia el día para verlas listadas aquí."
        testid={`${testid}-empty`}
      />
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="text-left">
              {["Hora", "Paciente", showArrived ? "Llegada" : null, "Motivo", ""]
                .filter((x) => x !== null)
                .map((h) => (
                <th
                  key={h}
                  className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orderedAppts.map((a) => {
              const p = patientById?.[a.patient_id];
              const name = p?.full_name || "Paciente";
              const isCompleted = a.status === "completed";
              const isCancelled = a.status === "cancelled";
              const isInProgress = a.status === "in_progress";
              const editable = !isCompleted && !isCancelled;
              return (
                <tr
                  key={a.id}
                  data-testid={`appt-row-${a.id}`}
                  className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
                >
                  <td className="py-3 pr-3 whitespace-nowrap">
                    <div className="flex flex-col leading-tight">
                      <span
                        className="text-[15px] font-bold text-black tabular-nums"
                        style={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {(() => {
                          const d = new Date(a.date);
                          return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                        })()}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-black/40 font-semibold">
                        {a.duration_min ? `${a.duration_min} min` : "—"}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-3">
                      {p?.photo_url ? (
                        <img
                          src={p.photo_url}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#F2F3F5] text-black text-[13px] font-bold flex items-center justify-center flex-shrink-0">
                          {initials(name)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold truncate max-w-[280px] text-[15px] text-black">
                          {name}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-[13px] text-black/55">
                          <span
                            className="inline-flex items-center gap-1.5 truncate max-w-[160px]"
                            data-testid={`appt-phone-${a.id}`}
                            title={p?.phone || "Sin teléfono"}
                          >
                            <Phone className="w-3.5 h-3.5 text-black/45 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                            {p?.phone ? (
                              <a
                                href={`tel:${p.phone}`}
                                className="hover:text-black hover:underline truncate"
                              >
                                {p.phone}
                              </a>
                            ) : (
                              <span className="text-black/30">—</span>
                            )}
                          </span>
                          <span
                            className="inline-flex items-center gap-1.5 truncate max-w-[220px]"
                            data-testid={`appt-email-${a.id}`}
                            title={p?.email || "Sin correo"}
                          >
                            <Mail className="w-3.5 h-3.5 text-black/45 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                            {p?.email ? (
                              <a
                                href={`mailto:${p.email}`}
                                className="hover:text-black hover:underline truncate"
                              >
                                {p.email}
                              </a>
                            ) : (
                              <span className="text-black/30">—</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  {showArrived && (
                    <td className="py-3 px-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => toggleArrived(a)}
                        disabled={isCompleted || isCancelled}
                        data-testid={`arrived-toggle-${a.id}`}
                        title={
                          a.arrived_at
                            ? `Paciente llegó (clic para quitar)`
                            : "Marcar como llegado"
                        }
                        className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed ${
                          a.arrived_at
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-[#F2F3F5] text-black/55 hover:bg-black/[0.08]"
                        }`}
                      >
                        {a.arrived_at ? "Llegó" : "No ha llegado"}
                      </button>
                    </td>
                  )}
                  <td className="py-3 px-3 text-[13px] text-black/70 truncate max-w-[280px]">
                    {a.reason || "—"}
                  </td>
                  <td className="py-3 pl-3 text-right">
                    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                      {isCompleted && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold">
                          <CheckCircle2 className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Atendida
                        </span>
                      )}
                      {isCancelled && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-red-50 text-red-700 font-semibold">
                          <XCircle className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Cancelada
                        </span>
                      )}
                      {isInProgress && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-amber-50 text-amber-700 font-semibold">
                          <Activity className="w-3 h-3" strokeWidth={1.75} weight="regular" /> En curso
                        </span>
                      )}
                      {!isCompleted && !isCancelled && a.patient_confirmation === "confirmed" && (
                        <span
                          data-testid={`confirmed-${a.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-black/[0.05] text-black/75 font-semibold"
                          title="Paciente confirmó asistencia"
                        >
                          <CheckCircle2 className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Confirmada
                        </span>
                      )}
                      {!isCompleted && !isCancelled && (!a.patient_confirmation || a.patient_confirmation === "pending") && (
                        <span
                          data-testid={`pending-confirm-${a.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-[#F2F3F5] text-black/55 font-semibold"
                          title="Paciente aún no confirmó"
                        >
                          <Clock className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Sin confirmar
                        </span>
                      )}
                      {editable && showIniciar && isInProgress && (
                        <Link
                          to={`/doctor/consulta/${a.id}`}
                          data-testid={`continue-${a.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-black text-white hover:bg-black/85 transition"
                        >
                          <Play className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Continuar
                        </Link>
                      )}
                      {editable && showIniciar && !isInProgress && (
                        <button
                          type="button"
                          onClick={() => startConsult(a)}
                          disabled={starting === a.id}
                          data-testid={`start-${a.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-black text-white hover:bg-black/85 transition disabled:opacity-60"
                        >
                          <Play className="w-3 h-3" strokeWidth={1.75} weight="regular" /> {starting === a.id ? "Iniciando…" : "Iniciar consulta"}
                        </button>
                      )}
                      {editable && (
                        <RowActionsMenu
                          triggerTestId={`actions-${a.id}`}
                          items={[
                            isInProgress && {
                              icon: CheckCircle2,
                              label: "Marcar atendida",
                              testId: `complete-${a.id}`,
                              onClick: () => markCompleted(a),
                              color: "slate",
                            },
                            !isCompleted &&
                              !isCancelled &&
                              a.patient_confirmation !== "confirmed" && {
                                icon: CheckCircle2,
                                label: "Confirmar cita",
                                testId: `confirm-${a.id}`,
                                onClick: () => confirmAppt(a),
                                color: "slate",
                              },
                            {
                              icon: Mail,
                              label: "Enviar recordatorio",
                              testId: `reminder-${a.id}`,
                              onClick: () => sendReminder(a),
                            },
                            {
                              icon: CalendarClock,
                              label: "Reprogramar",
                              testId: `reschedule-${a.id}`,
                              onClick: () => openReschedule(a),
                            },
                            {
                              icon: XCircle,
                              label: "Cancelar",
                              testId: `cancel-${a.id}`,
                              onClick: () => cancel(a),
                              color: "red",
                            },
                            { divider: true },
                            {
                              icon: Trash2,
                              label: "Eliminar",
                              testId: `delete-${a.id}`,
                              onClick: () => del(a),
                            },
                          ].filter(Boolean)}
                        />
                      )}
                      {!editable && (
                        <div className="inline-flex items-center gap-1.5">
                          {isCompleted && !paidAppointmentIds?.has(a.id) && (
                            <button
                              type="button"
                              onClick={() => setCharging(a)}
                              data-testid={`cobrar-${a.id}`}
                              title="Cobrar esta cita"
                              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-black text-white hover:bg-black/85 transition"
                            >
                              <DollarSign className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                              Cobrar
                            </button>
                          )}
                          {isCompleted && paidAppointmentIds?.has(a.id) && (
                            <span
                              data-testid={`paid-${a.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-[#F2F3F5] text-black/60"
                              title="Cita cobrada"
                            >
                              <CheckCircle2 className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                              Cobrada
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => del(a)}
                            data-testid={`delete-${a.id}`}
                            title="Eliminar"
                            className="text-black/40 hover:text-red-500 transition p-1"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {reschedule && (
        <Portal>
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div
              className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl w-full max-w-md"
              data-testid="reschedule-modal"
            >
              <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-black/[0.05]">
                <div>
                  <div className="text-[15px] font-semibold text-black">
                    Reprogramar cita
                  </div>
                  <div className="text-[11px] text-black/45 mt-0.5">
                    {patientById?.[reschedule.patient_id]?.full_name ||
                      "Paciente"}{" "}
                    · actualmente:{" "}
                    {new Date(reschedule.date).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                    Nueva fecha y hora
                  </label>
                  <div className="mt-1.5">
                    <DateTimePicker
                      value={newDate}
                      onChange={setNewDate}
                      testid="reschedule-datetime"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setReschedule(null);
                      setNewDate("");
                    }}
                    className="h-10 px-4 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[13px] font-semibold text-black transition"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmReschedule}
                    className="h-10 px-5 rounded-lg bg-black text-white hover:bg-black/85 text-[13px] font-semibold transition"
                    data-testid="reschedule-confirm"
                  >
                    Reprogramar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {charging && (
        <PaymentModal
          appt={charging}
          patient={patientById?.[charging.patient_id]}
          onClose={() => setCharging(null)}
          onSaved={() => {
            setCharging(null);
            onChanged?.();
          }}
        />
      )}
    </>
  );
}

export function PaymentModal({ appt, patient, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [concept, setConcept] = useState(appt?.reason || "Consulta");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (isNaN(n) || n <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setSaving(true);
    try {
      await api.post("/payments", {
        patient_id: appt.patient_id,
        appointment_id: appt.id,
        amount: n,
        method,
        concept: concept.trim() || "Consulta",
        notes: notes.trim() || null,
        status: "paid",
      });
      toast.success("Pago registrado");
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "No pude registrar el pago");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 fade-up"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <form
          onSubmit={submit}
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col"
          data-testid="payment-modal"
        >
          <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-black/[0.05]">
            <div>
              <div className="text-[15px] font-semibold text-black">Cobrar cita</div>
              <div className="text-[11px] text-black/45 mt-0.5">
                {patient?.full_name || "Paciente"} · {new Date(appt.date).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-black/[0.05] flex items-center justify-center text-black/60 transition"
            >
              <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto no-scrollbar flex-1">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Monto *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-black/45">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  className="w-full h-10 pl-7 pr-3 rounded-lg border border-black/[0.08] bg-white text-[13px] font-semibold text-black focus:outline-none focus:border-black/40 transition"
                  data-testid="pay-amount"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Método de pago
              </label>
              <div className="grid grid-cols-4 gap-1 p-1 bg-[#F2F3F5] rounded-lg">
                {[
                  { v: "cash", l: "Efectivo" },
                  { v: "card", l: "Tarjeta" },
                  { v: "transfer", l: "Transf." },
                  { v: "other", l: "Otro" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setMethod(o.v)}
                    data-testid={`pay-method-${o.v}`}
                    className={`h-8 rounded-md text-[12px] font-semibold transition ${
                      method === o.v
                        ? "bg-white text-black shadow-sm"
                        : "text-black/55 hover:text-black/80"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Concepto
              </label>
              <input
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-black/[0.08] bg-white text-[13px] text-black focus:outline-none focus:border-black/40 transition"
                data-testid="pay-concept"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Notas (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Observaciones del cobro"
                className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-black focus:outline-none focus:border-black/40 transition resize-none"
                data-testid="pay-notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 px-5 py-3 border-t border-black/[0.05] bg-white">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[13px] font-semibold text-black transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="pay-submit"
              className="h-10 px-5 rounded-lg bg-black text-white hover:bg-black/85 text-[13px] font-semibold transition disabled:opacity-60"
            >
              {saving ? "Registrando…" : "Registrar cobro"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

